import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { uploadChunked } from "@/lib/chunkedUpload";
import { applyProfile, applyFavicon, logoSrc } from "@/context/BrandingContext";
import { Button } from "@/components/ui/button";
import { UploadCloud, CheckCircle2, AlertTriangle, Loader2 } from "lucide-react";

const ACCEPT = { grafik: ".pdf,.svg,.ai,.eps,.png,.webp,.jpg,.jpeg", video: ".mp4,.mov,.m4v,.webm" };

function SpaceUpload({ token, s, maxMb, onDone }) {
  const [busy, setBusy] = useState(false);
  const [pct, setPct] = useState(0);
  const [warn, setWarn] = useState([]);
  const [error, setError] = useState("");
  const input = useRef(null);
  const send = async (f) => {
    if (!f) return;
    setError("");
    if (f.size > maxMb * 1048576) { setError(`Die Datei ist größer als ${maxMb} MB.`); return; }
    setBusy(true); setPct(0); setWarn([]);
    try { const r = await uploadChunked(`/public/logo/${token}/${s.id}`, f, setPct); setWarn(r?.data?.warnings || []); onDone(); } catch (e) { setError(apiError(e.response?.data?.detail)); } finally { setBusy(false); }
  };
  return (
    <div className="rounded-xl border border-slate-200 p-4" data-testid={`logo-space-${s.id}`}>
      <div className="font-semibold text-slate-900">{s.type}: {s.name}{s.location ? ` · ${s.location}` : ""}</div>
      {s.requirements && <p className="text-sm text-slate-600 mt-1 whitespace-pre-wrap">{s.requirements}</p>}
      {warn.length > 0 && <p className="text-sm text-amber-700 mt-2" data-testid={`logo-warn-${s.id}`}>Ihr Video wurde gespeichert, weicht aber von unseren Vorgaben ab: {warn.join(" · ")}. Bitte prüfen Sie die Datei und laden Sie gegebenenfalls eine angepasste Fassung hoch.</p>}
      <div className="mt-3 flex flex-wrap items-center gap-3">
        <Button onClick={() => input.current?.click()} disabled={busy} data-testid={`logo-upload-${s.id}`}>
          {busy ? <Loader2 size={16} className="animate-spin mr-1.5" /> : <UploadCloud size={16} className="mr-1.5" />}{busy ? `Wird hochgeladen … ${pct} %` : s.uploaded_at ? "Neue Datei hochladen" : s.media === "video" ? "Video hochladen" : "Logo hochladen"}
        </Button>
        {s.uploaded_at && <span className="text-sm text-emerald-700 flex items-center gap-1" data-testid={`logo-done-${s.id}`}><CheckCircle2 size={15} /> erhalten am {new Date(s.uploaded_at).toLocaleDateString("de-DE")}</span>}
        <input ref={input} type="file" accept={ACCEPT[s.media] || ACCEPT.grafik} className="hidden" onChange={(e) => { send(e.target.files[0]); e.target.value = ""; }} data-testid={`logo-input-${s.id}`} />
      </div>
      {error && <div className="mt-2 text-sm text-red-600 flex items-center gap-1.5"><AlertTriangle size={15} /> {error}</div>}
    </div>
  );
}

export default function LogoUpload() {
  const { token } = useParams();
  const [info, setInfo] = useState(null);
  const [error, setError] = useState("");
  const [brand, setBrand] = useState(null);
  const load = () => api.get(`/public/logo/${token}`).then((r) => setInfo(r.data)).catch((e) => setError(apiError(e.response?.data?.detail)));
  useEffect(() => {
    api.get("/branding/public").then((r) => { setBrand(r.data); applyProfile(r.data.profile); applyFavicon(r.data.favicon_url, r.data.favicon_svg_url); }).catch(() => {});
    load();
  }, [token]); // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <div className="min-h-screen bg-slate-50 flex items-start justify-center p-6" data-testid="logo-upload-page">
      <div className="bg-white rounded-2xl shadow-sm border border-slate-200 w-full max-w-2xl p-8 space-y-5">
        {(brand?.logos?.pdf || brand?.logos?.sidebar) && <img src={logoSrc(brand.logos.pdf || brand.logos.sidebar)} alt="" className="h-12" data-testid="logo-upload-brand" />}
        {error ? <p className="text-red-600" data-testid="logo-upload-error">{error}</p> : !info ? <Loader2 className="animate-spin" /> : <>
          <div>
            <h1 className="font-display text-2xl font-bold text-slate-900">Logo für Ihre Werbefläche</h1>
            <p className="text-slate-500 mt-1 text-sm">{info.customer} – bitte laden Sie je Fläche Ihr Logo hoch (PDF, AI, EPS, SVG, PNG oder JPG; für die Medienstele auch Videos als MP4 oder MOV; max. {info.max_mb} MB). Sie können eine Datei jederzeit ersetzen.</p>
          </div>
          {info.spaces.map((s) => <SpaceUpload key={s.id} token={token} s={s} maxMb={info.max_mb} onDone={load} />)}
          {info.spaces.length === 0 && <p className="text-slate-500">Für Sie sind aktuell keine Werbeflächen hinterlegt.</p>}
        </>}
      </div>
    </div>
  );
}
